import { navigate } from 'gatsby';
import PropTypes from 'prop-types';
import React from 'react';
import { useInView } from 'react-intersection-observer';

import { addTrailingSlash } from '../../tools/textTools';
import ContentfulImage from '../Common/ContentfulImage';
import { ArrowLink } from '../Common/Links';
import RichTextConverter from '../Common/RichText/RichTextConverter';
import PageSection from '../PageSections/PageSection';
import * as styles from './LargeCards.module.scss';

export const LargeCard = React.memo(
  ({
    imageFragment,
    type,
    title,
    description,
    url,
    ctaLabel,
    backgroundColor,
    fullImage = false,
    style,
    forceNewTab = false,
    className,
    ...rest
  }) => {
    const onCard = (e) => {
      e.preventDefault();
      if (typeof window === 'undefined') return;
      if (!forceNewTab) {
        navigate(url);
      } else {
        window.open(url, '_blank');
      }
    };
    const slug = addTrailingSlash(url);

    return (
      /*eslint-disable-next-line */
      <div
        className={[styles.postCard, slug ? styles.postCardLink : '', styles[backgroundColor], className].join(' ')}
        onClick={slug ? onCard : null}
        style={style}
        {...rest}
        data-analytics-component={'large-card'}
        data-analytics-id={title}
      >
        <div className={[styles.cardImage, fullImage ? styles.fullImage : ''].join(' ')}>
          <ContentfulImage alt={title} imageFragment={imageFragment} />
        </div>
        {type && <div className={styles.cardType}>{type}</div>}
        <h4 className={styles.title}>{title}</h4>
        {typeof description === 'string' ? <p className={styles.description}>{description}</p> : description}
        {slug && ctaLabel && (
          <div className={styles.postLink}>
            <ArrowLink label={ctaLabel} url={slug} isExternal={forceNewTab} />
          </div>
        )}
      </div>
    );
  }
);

const LargeCards = React.memo(
  ({
    section,
    className,
    cardClassName,
    noPad = false,
    fullImage = false,
    wide = false,
    cardBackground,
    animate = false,
    opacityOverride,
    forceNewTab = false,
    hideCopy = false,
    ctaClassName,
  }) => {
    const { ref: containerRef, inView: isVisible } = useInView({
      threshold: 0.25,
      triggerOnce: true,
    });
    let opacity = animate ? (isVisible ? '1' : '0') : '1';
    if (opacityOverride && !isNaN(opacityOverride)) {
      opacity = opacityOverride;
    }
    const hasCTA = section.content.filter((c) => !!c.linkUrl || c.slug).length > 0;
    let cards = section.content.map((card, i) => {
      switch (card.__typename) {
        case 'ContentfulComponentSimpleContent': {
          const text = card.body?.raw ? <RichTextConverter copy={card.body} /> : '';
          return (
            <LargeCard
              fullImage={fullImage}
              key={i}
              imageFragment={card.image}
              url={card.linkUrl}
              ctaLabel={card.linkLabel}
              title={card.title}
              description={text}
              type={card.subtitle}
              backgroundColor={card.accentColor}
              style={{ opacity: opacity, '--delay': `${i * 100}ms` }}
              forceNewTab={forceNewTab}
              className={cardClassName}
            />
          );
        }
        case 'ContentfulPageCaseStudy': {
          const description = card.description?.description || card.pageMetaData?.metaDescription?.metaDescription;
          return (
            <LargeCard
              key={i}
              imageFragment={card.pageMeta?.ogImage || card.pageMetaData?.ogImage || card.attachmentImage}
              url={`/${card.slug}`}
              ctaLabel="Read the story"
              title={card.heading}
              description={description}
              type="Customer Story"
              style={{ opacity: opacity, '--delay': `${i * 100}ms` }}
              forceNewTab={forceNewTab}
              className={cardClassName}
            />
          );
        }
        case 'ContentfulPageBlogPost':
        case 'ContentfulPageWalledResource':
        case 'ContentfulPageEvent':
        case 'ContentfulPageResource': {
          let label = card.contentType?.viewLabel;
          let typeTitle = card.contentType?.title;
          let description = card.articleDescription?.articleDescription;
          if (card.__typename === 'ContentfulPageWalledResource') {
            description = card.description?.description;
          } else if (card.__typename === 'ContentfulPageEvent') {
            description = card.listingDescription?.listingDescription;
          }
          if (!label) {
            label = 'View more';
          }
          if (!typeTitle && card.__typename === 'ContentfulPageBlogPost') {
            typeTitle = 'BLOG POST';
          } else if (
            !typeTitle &&
            (card.__typename === 'ContentfulPageResource' ||
              card.__typename === 'ContentfulPageWalledResource' ||
              card.__typename === 'ContentfulPageEvent')
          ) {
            typeTitle = 'RESOURCE';
          }
          return (
            <LargeCard
              key={i}
              imageFragment={card.pageMeta?.ogImage || card.pageMetaData?.ogImage || card.heroImage}
              url={card.__typename === 'ContentfulPageBlogPost' ? `/blog/${card.slug}` : `/resources/${card.slug}`}
              ctaLabel={label}
              title={card.title}
              description={description}
              type={typeTitle}
              backgroundColor={cardBackground}
              style={{ opacity: opacity, '--delay': `${i * 100}ms` }}
              forceNewTab={forceNewTab}
              className={cardClassName}
            />
          );
        }
        default:
          return null;
      }
    });

    const columns = { '--column-count': section.itemsPerRow || 3 };

    return (
      <PageSection slim wide={wide} className={className} data-analytics-section={'large-cards'}>
        <div className={wide ? styles.container : ''}>
          {section.headingSize === 'H3' && <h3>{section.heading}</h3>}
          {section.headingSize !== 'H3' && <h2>{section.heading}</h2>}
          {section.sectionCopy && <RichTextConverter className={styles.shortText} copy={section.sectionCopy} />}
          <div
            ref={containerRef}
            style={columns}
            className={[styles.grid, noPad && !hasCTA ? '' : styles.pad].join(' ')}
          >
            {cards}
          </div>
        </div>
        {section.ctaLabel && section.ctaUrl && (
          <div className={[styles.cta, ctaClassName].join(' ')}>
            <ArrowLink label={section.ctaLabel} url={section.ctaUrl} />
          </div>
        )}
      </PageSection>
    );
  }
);

LargeCards.propTypes = {
  section: PropTypes.object.isRequired,
  className: PropTypes.string,
};

export default LargeCards;
